import { useState, useRef, useEffect } from 'react';

/**
 * Dropdown of checkboxes for one filter field. Several ticked values are
 * OR'ed together ("Vendor is A or B"); different fields are AND'ed by the
 * page. Long lists (vendors, contractors) get a search box.
 *
 * Values are kept in the URL as a comma-separated list - see listParam()
 * and listValue() below.
 */
export default function MultiSelectFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const q = query.trim().toLowerCase();
  const visible = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;

  function toggle(value) {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  }

  return (
    <div className="multi-filter" ref={ref}>
      <button type="button" className={'multi-filter-button' + (selected.length ? ' active' : '')} onClick={() => setOpen(o => !o)}>
        {label}{selected.length > 0 && <span className="multi-filter-count">{selected.length}</span>}
        <span aria-hidden="true" style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div className="multi-filter-menu">
          {options.length > 8 && (
            <input className="multi-filter-search" autoFocus placeholder={`Search ${label.toLowerCase()}…`} value={query} onChange={e => setQuery(e.target.value)} />
          )}
          <div className="multi-filter-options">
            {visible.length === 0 && <div className="multi-filter-empty">No matches</div>}
            {visible.map(o => (
              <label key={o.value} className="multi-filter-option">
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
                {o.count != null && <span className="multi-filter-option-count">{o.count}</span>}
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%' }} onClick={() => onChange([])}>Clear {label.toLowerCase()}</button>
          )}
        </div>
      )}
    </div>
  );
}

// URL <-> list helpers: ?vendor=3,17 <-> ['3', '17']
export function listValue(raw) {
  return raw ? String(raw).split(',').filter(Boolean) : [];
}
export function listParam(values) {
  return values.length ? values.join(',') : '';
}
